'use client';

import { StyledTooltipContainer } from '@components/InternalIcons/Tooltip.styled';
import { openInternalPopup } from '@components/InternalIcons/utils/internalPopup';
import { Flex, IconButton } from '@coolblue-development/freddy';
import selectOptionTrigger from '@coolblue-development/ga4-events/triggers/InternalIcons/selectOptionTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { ReactElement } from 'react';

export default function VanessaMainIcon({
  url,
  index,
  totalIndices
}: {
  url: string;
  index: number;
  totalIndices: number;
}): ReactElement {
  const iconLabel = 'Open product in Vanessa';
  const { trackEvent } = useAnalytics();

  return (
    <Flex alignItems='center'>
      <StyledTooltipContainer
        data-internal-icons-tooltip={iconLabel}
        data-testid='vanessa-main-icon'
      >
        <IconButton
          aria-label={iconLabel}
          size='small'
          onClick={() => {
            trackEvent(
              selectOptionTrigger({
                sub_feature: 'vanessa',
                index: index,
                total_indices: totalIndices,
                selection_type: 'selected',
                option_name: 'vanessa',
                attribute: 'vanessa'
              })
            );
            openInternalPopup(url);
          }}
          sx={theme => ({
            padding: '0.6rem',
            svg: {
              width: theme.spacing[500],
              height: theme.spacing[500]
            }
          })}
        >
          <IconElement />
        </IconButton>
      </StyledTooltipContainer>
    </Flex>
  );
}

const IconElement = () => {
  return (
    <svg width='21' height='21' viewBox='0 0 21 21' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <g clipPath='url(#clip0_87_30718)'>
        <mask
          id='mask0_87_30718'
          style={{ maskType: 'luminance' }}
          maskUnits='userSpaceOnUse'
          x='0'
          y='0'
          width='21'
          height='21'
        >
          <path d='M20.5 0.5H0.5V20.5H20.5V0.5Z' fill='white' />
        </mask>
        <g mask='url(#mask0_87_30718)'>
          <path
            d='M2.5 12.01C2.5 16.43 6.08 20.01 10.5 20.01C14.92 20.01 18.5 16.43 18.5 12.01C18.5 7.59001 14.92 4.01001 10.5 4.01001C6.08 4.01001 2.5 7.60001 2.5 12.01Z'
            fill='#CCE9F9'
          />
          <path
            d='M12.69 16.72C12.69 16.72 11.23 16.05 10.55 16.2C9.87001 16.36 8.51001 17.08 8.51001 17.08L9.97001 20.03L12.41 19.13L12.69 16.72Z'
            fill='#285DAB'
          />
          <path
            d='M11.66 17.6799C11.15 15.9499 11.46 13.4199 11.46 13.4199H9.83001C9.83001 13.4199 10.14 15.4899 9.83001 17.7299C9.71001 18.6399 9.37001 19.3399 9.01001 19.8799C9.49001 19.9699 9.99001 20.0199 10.5 20.0199C11.38 20.0199 12.23 19.8699 13.02 19.6099C12.45 19.0999 11.88 18.4399 11.65 17.6899L11.66 17.6799Z'
            fill='#F8A98F'
          />
          <path
            d='M8.78995 15.3399L7.49995 14.7799C7.49995 14.7799 8.04995 16.2499 7.27995 17.2799C6.99995 17.6499 6.67995 18.2499 6.56995 18.9699C7.51995 19.5099 8.59995 19.8599 9.73995 19.9699C9.75995 19.7699 9.75995 19.5499 9.73995 19.2999C9.57995 17.3699 7.98995 17.1999 8.79995 15.3399H8.78995Z'
            fill='#F9C823'
          />
          <g opacity='0.5'>
            <g style={{ mixBlendMode: 'multiply' }}>
              <path
                d='M9.85999 13.83H11.45C11.45 13.83 11.32 14.49 11.37 15.87L9.97999 15.42C9.94999 14.32 9.85999 13.83 9.85999 13.83Z'
                fill='#B3B3B3'
              />
            </g>
          </g>
          <path
            d='M11.7101 19.91C12.3201 19.82 12.9101 19.66 13.4601 19.44L12.7201 18.53V16.71L11.7101 19.91Z'
            fill='#0090E3'
          />
          <path
            d='M10.5001 20.01C10.5001 20.01 10.5201 20.01 10.5301 20.01L8.76006 16.95L8.81006 18.8099L8.31006 19.7C9.01006 19.9 9.74006 20.01 10.5101 20.01H10.5001Z'
            fill='#0090E3'
          />
          <path
            d='M7.99996 1.13997C6.12996 1.78997 2.77996 7.72997 2.94996 10.76C3.06996 12.83 8.07996 15.07 8.26996 17.53C8.60996 14.74 11.13 14.27 11.13 14.27L10.99 13.65C10.99 13.65 19.42 13.13 18.07 8.22997C16.77 3.55997 10.86 0.149966 7.99996 1.13997Z'
            fill='#F9C823'
          />
          <path
            d='M6.23999 10C6.45999 12.62 8.38999 14.73 11.64 14.32C14.89 13.91 15.95 11.82 15.73 9.19001C15.51 6.57001 13.2 4.63001 10.58 4.85001C7.95999 5.07001 6.00999 7.38001 6.23999 10Z'
            fill='#F8A98F'
          />
          <path
            d='M7.79004 10.6799C8.99004 12.8199 11.87 12.8099 11.87 12.8099C11.87 12.8099 8.98004 14.8399 7.79004 10.6799Z'
            fill='#C93A72'
          />
          <path
            d='M5.30996 9.80992C5.50996 10.5699 6.29996 11.2599 6.64996 11.4499C7.07996 11.3399 7.10996 10.4299 6.90996 9.65992C6.70996 8.89992 6.07996 8.35992 5.65996 8.46992C5.23996 8.57992 5.11996 9.03992 5.31996 9.79992L5.30996 9.80992Z'
            fill='#F8A98F'
          />
          <path
            d='M6.56995 7.74994C6.56995 7.74994 8.97995 6.60994 10.0599 4.83994C10.0599 4.83994 17.3399 6.47994 15.5899 14.3799C18.8799 9.79994 16.5499 5.35994 16.5499 5.35994L11.3399 1.93994L7.12995 3.47994L6.56995 7.75994V7.74994Z'
            fill='#F9C823'
          />
        </g>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M10.1217 4.6778L10.06 4.84033C10.082 4.74277 10.1217 4.6778 10.1217 4.6778L10.1235 4.67819L10.1278 4.67921L10.1443 4.68314C10.1586 4.68663 10.1795 4.69187 10.2066 4.699C10.2607 4.71327 10.3394 4.73511 10.4386 4.76568C10.637 4.82681 10.9178 4.92285 11.2489 5.06294C11.9107 5.34295 12.776 5.79988 13.5868 6.50751C14.398 7.21554 15.1554 8.17519 15.5985 9.45929C16.0417 10.7437 16.198 12.1378 15.7571 14.1283L15.4862 14.5384C15.9203 12.579 15.7993 10.8345 15.3697 9.58951C14.94 8.34424 14.2055 7.41264 13.4155 6.72317C12.6251 6.0333 11.7797 5.58648 11.1312 5.31211C10.8072 5.17502 10.5329 5.08122 10.34 5.0218C10.2436 4.99209 10.1675 4.97099 10.1159 4.95737C10.1098 4.95578 10.1041 4.9543 10.0988 4.95291C9.53743 5.66587 9.0622 6.18836 8.53489 6.62929C7.9948 7.08091 7.40344 7.44417 6.61486 7.83971L6.57504 7.76033L6.4978 7.61325C7.27804 7.22189 7.79014 6.9014 8.31446 6.46297C8.83908 6.02427 9.31447 5.49955 9.88916 4.76578L9.96789 4.651L10.1217 4.6778Z'
          fill='#C67B12'
        />
      </g>
      <defs>
        <clipPath id='clip0_87_30718'>
          <rect width='20' height='20' fill='white' transform='translate(0.5 0.5)' />
        </clipPath>
      </defs>
    </svg>
  );
};
